import { map } from "lodash-es";
import { useId, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerFooter,
  DrawerTopBar,
  DrawerTopBarActionButton,
} from "@/components/Drawer";
import { PriceRangeSlider } from "@/components/Sliders/PriceRangeSlider";
import type {
  FlightFilters,
  FlightsSortValue,
} from "@/features/flights/types/types";
import { initialSortValue } from "@/features/flights/utils/initialFlightsValues";
import { useToggleSelectAll } from "@/hooks/useToggleSelectAll";
import { FlightFilterTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";

import { AirlinesFilter } from "./AirlinesFilter";
import { FlightsSort } from "./FlightsSort";
import { StopsFilter } from "./StopsFilter";

type Props = {
  defaultFilters: FlightFilters;
  filters: FlightFilters;
  sortValue: FlightsSortValue;
  onFiltersChange: (
    newFilters: Partial<FlightFilters>,
    newSort: FlightsSortValue,
  ) => void;
  onClose: () => void;
};

export function FlightsFilters({
  defaultFilters,
  filters,
  sortValue,
  onFiltersChange,
  onClose,
}: Props) {
  const priceHeaderId = useId();
  const intl = useIntl();
  // Make local copy of filters
  const [localFilters, setLocalFilters] = useState<FlightFilters>({
    ...filters,
  });
  const [currentSort, setCurrentSort] = useState<FlightsSortValue>(sortValue);

  const allAirlinesCodes = map(localFilters.airlines, "code");
  const allDefaultAirlinesCodes = map(defaultFilters.airlines, "code");

  const {
    manuallySelectedAll,
    setManuallySelectedAll,
    areAllSelected,
    toggle,
  } = useToggleSelectAll<string>({
    selected: localFilters.selectedAirlines,
    all: allAirlinesCodes,
  });

  const areAirlinesInDefaultState = areAllSelected && !manuallySelectedAll;

  return (
    <>
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Filters",
          description:
            "Button or heading to open filter and sort options for flight results",
        })}
        actionElement={
          <DrawerTopBarActionButton
            onClick={() => {
              setLocalFilters(defaultFilters);
              setCurrentSort(initialSortValue);
              setManuallySelectedAll(false);
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Clear filters",
              description: "Button to remove all active filters at once",
            })}
          </DrawerTopBarActionButton>
        }
      />

      <DrawerContent>
        <div className="flex flex-grow flex-col gap-5">
          <div>
            <h3 className="mb-2 text-lg font-semibold text-black">
              {intl.formatMessage({
                defaultMessage: "Sorting",
                description:
                  "Button or label to open sort options for flight results",
              })}
            </h3>
            <FlightsSort
              value={currentSort}
              onChange={(newSort) => {
                setCurrentSort(newSort);
              }}
            />
          </div>

          <div>
            <h3
              id={priceHeaderId}
              className="mb-2 text-lg font-semibold text-black"
            >
              {intl.formatMessage({
                defaultMessage: "Price Range (IQD)",
                description:
                  "Filter label for the price range slider (values in IQD)",
              })}
            </h3>
            <PriceRangeSlider
              min={defaultFilters.priceRange[0]}
              max={defaultFilters.priceRange[1]}
              aria-labelledby={priceHeaderId}
              value={[localFilters.priceRange[0], localFilters.priceRange[1]]}
              onChange={(newValue) => {
                setLocalFilters((prevFilters) => ({
                  ...prevFilters,
                  priceRange: newValue,
                }));
              }}
            />
          </div>

          <div>
            <h3 className="mb-2 text-lg font-semibold text-black">
              {intl.formatMessage({
                defaultMessage: "Stops",
                description: "Filter label for the stops / layovers filter",
              })}
            </h3>
            <StopsFilter
              value={localFilters.stops}
              onChange={(newValue) => {
                // ensure that a value is selected
                if (newValue == null) {
                  return;
                }
                setLocalFilters((prevFilters) => ({
                  ...prevFilters,
                  stops: newValue,
                }));
              }}
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="shrink-0 text-lg font-semibold text-black">
                {intl.formatMessage({
                  defaultMessage: "Airlines",
                  description:
                    "Filter section label for filtering flights by airline",
                })}
              </h3>
              {localFilters.airlines.length > 1 ? (
                <Button
                  onClick={(e) => {
                    if (areAirlinesInDefaultState) {
                      setLocalFilters((prevFilters) => ({
                        ...prevFilters,
                        selectedAirlines: allAirlinesCodes,
                      }));
                      setManuallySelectedAll(true);
                    } else {
                      setLocalFilters((prevFilters) => ({
                        ...prevFilters,
                        selectedAirlines: allDefaultAirlinesCodes,
                      }));
                      setManuallySelectedAll(false);
                    }

                    // Remove focus from the button after click
                    e.currentTarget.blur();
                  }}
                  variant="textFit"
                  size="small"
                >
                  {areAirlinesInDefaultState
                    ? intl.formatMessage({
                        defaultMessage: "Select All",
                        description:
                          "Button to select all items in a list at once",
                      })
                    : intl.formatMessage({
                        defaultMessage: "Clear filter",
                        description: "Button to remove a single active filter",
                      })}
                </Button>
              ) : null}
            </div>
            <AirlinesFilter
              airlines={localFilters.airlines}
              selectedAirlines={localFilters.selectedAirlines}
              userManuallySelectedAllAirlines={manuallySelectedAll}
              onSingleAirlineChange={(e, checked, airline) => {
                e.stopPropagation();
                const newSelected = toggle(
                  localFilters.selectedAirlines,
                  airline.code,
                  checked,
                );
                setLocalFilters((prev) => ({
                  ...prev,
                  selectedAirlines: newSelected,
                }));
              }}
            />
          </div>
        </div>
      </DrawerContent>

      <DrawerFooter>
        <Button
          fullWidth
          onClick={() => {
            rudderAnalytics.track(
              FlightFilterTrackingEvents.USER_APPLIED_FLIGHTS_FILTER,
              {
                source: "all",
                sort_type: currentSort.order,
                min_price: localFilters.priceRange[0],
                max_price: localFilters.priceRange[1],
                stops: localFilters.stops,
                airlines: localFilters.selectedAirlines,
              },
            );
            onFiltersChange(localFilters, currentSort);
          }}
          variant="contained"
          size="large"
        >
          {intl.formatMessage({
            defaultMessage: "Apply",
            description: "Button to apply selected filters or changes",
          })}
        </Button>
      </DrawerFooter>
    </>
  );
}
